import { useFormContext } from "react-hook-form";
import { notifyBugsnag } from "@/react/helpers/bugsnagHelpers";
import { useEmailValidation } from "@circle-react/components/OnboardingPage/CreateProfilePage/useEmailValidation";
import { usePaywallCheckoutContext } from "@circle-react/contexts/Paywalls/paywallCheckoutContext";
import { isCard } from "@circle-react/helpers/paywallCheckoutHelpers";
import {
  GENERAL_ERROR,
  PIX_MODAL_CLOSED,
  buildErrorMessage,
  getReferral,
} from "../helpers";
import { useBuyNowPayLater } from "./paymentMethods/useBuyNowPayLater";
import { useCard } from "./paymentMethods/useCard";
import { usePix } from "./paymentMethods/usePix";

const manageError = (error: any, handleError: any, options: any) => {
  if (error?.cause?.error_details === PIX_MODAL_CLOSED.error_details) return;

  notifyBugsnag(error);
  console.error("Error: unexpected error occurred: ", error);

  const errObj = {
    message: error.message,
    error_details: error?.cause?.error_details || GENERAL_ERROR,
  };
  handleError(buildErrorMessage(errObj, options));
};

export const useOnSubmit = () => {
  const {
    handleError,
    setIsProcessingPayment,
    loginUrl,
    resetPasswordRedirectUrl,
    stripePaymentMethodType,
    clearAlerts,
    currentUser,
    previewMutation,
  } = usePaywallCheckoutContext();
  const cardHooks = useCard();
  const pixHooks = usePix();
  const buyNowPayLaterHooks = useBuyNowPayLater();

  const { validateEmail } = useEmailValidation({ shouldUseDebounce: false });
  const { setError, clearErrors, setFocus } = useFormContext();

  const paymentMethodHookStrategies: any = {
    pix: pixHooks,
    card: cardHooks,
    link: cardHooks,
    klarna: buyNowPayLaterHooks,
    afterpay_clearpay: buyNowPayLaterHooks,
    affirm: buyNowPayLaterHooks,
    google_pay: cardHooks,
    apple_pay: cardHooks,
    // EU Payment Methods
    sepa_debit: cardHooks,
    ideal: cardHooks,
    bancontact: cardHooks,
  };

  const performMailgunValidation = async (email: string) => {
    if (!currentUser && email) {
      clearErrors("email");
      const validationResult = await validateEmail(email);

      if (validationResult !== true) {
        setError("email", {
          type: "manual",
        });
        setFocus("email");
        return false;
      }
    }
    return true;
  };

  const onSubmit = async (formData: any) => {
    formData.coupon_code =
      previewMutation?.variables?.queryParams?.coupon_code || "";

    clearAlerts();

    if (getReferral()) {
      formData.referral_metadata = getReferral();
    }

    const validPaymentMethodType = isCard(formData.payment_method_type)
      ? stripePaymentMethodType
      : formData.payment_method_type;

    if (!paymentMethodHookStrategies[validPaymentMethodType]) {
      throw new Error(`Invalid payment method type ${validPaymentMethodType}`);
    }

    const { onSubmitMutation } =
      paymentMethodHookStrategies[validPaymentMethodType];

    try {
      setIsProcessingPayment(true);

      const isEmailValid = await performMailgunValidation(formData.email);
      if (!isEmailValid) return;

      await onSubmitMutation.mutateAsync(formData);
    } catch (error: any) {
      manageError(error, handleError, { loginUrl, resetPasswordRedirectUrl });
    } finally {
      setIsProcessingPayment(false);
    }
  };

  return {
    onSubmit,
  };
};
